import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import {
  LoginPage,
  AdminLoginPage,
  ZohoCallbackPage,
  ProtectedRoute,
  RequireAccountingBackend,
  UnauthorizedPage,
  ClientOnlyRestrictedPage,
  FromMasterPage,
  AuthProvider,
} from "./features/auth";
import { DashboardPage, CFODashboardPage } from "./features/dashboard";
import {
  TrialBalanceUploadPage,
  IncomeStatementReportsPage,
  ReportsPage,
  UploadRegistersPage,
  PivotViewLibraryPage,
  CreatePivotBuilderPage,
  ApplyPivotViewPage,
  EditPivotViewPage,
  RegisterViewPage,
  ReportingPage,
} from "./features/cfo";
import { DeleteGlsPage } from "./features/cfo/presentation/pages/DeleteGlsPage";
import { OcrQualityDashboardPage } from "./features/cfo/presentation/pages/OcrQualityDashboardPage";
import {
  AllInvoicesPage,
  InvoiceReviewPage,
  CreateManualInvoicePage,
  AddCustomerPage,
  EditCustomerPage,
} from "./features/invoices";
import {
  BillsReviewPage,
  CreateManualBillPage,
  AddNewPartyPage,
  EditPartyPage,
  AllBillsPage,
} from "./features/bills";
import { AgentReviewPage } from "./features/agent-review";
import {
  CustomerCreditsPage,
  VendorCreditsPage,
  CustomerOutstandingInvoicesPage,
  VendorOutstandingBillsPage,
} from "./features/reco";
import { HomePage } from "./features/home";
import {
  CompanyDetailsPage,
  ChartOfAccountsPage,
  GstPage,
  LocationsPage,
  LutPage,
  TdsTcsPage,
  BanksPage,
  CustomersVendorsPage,
  CurrencyPage,
  TeamAssignmentPage,
  EscalationsAccessPage,
  SETTINGS_DEFAULT_PATH,
} from "./features/settings";
import { AppShellV1 } from "./layouts/v1";
import { TallyConnectorPage } from "./features/admin/presentation/pages/TallyConnectorPage";
import { CreditNoteReviewPage } from "./features/credit";
import { DebitNoteReviewPage } from "./features/debit";
import { QueriesSection } from "./shared/queries/presentation/pages";
import { ClientQueriesSection } from "./features/client/queries/presentation/pages";
import { ClientComplianceDashboard } from "./features/client/compliance/presentation/pages";
import NewJournalEntry from "./pages/NewJournalEntry";
import EditJournalPage from "./pages/EditJournalPage";
import JournalDetailPage from "./pages/JournalDetailPage";
import RecurringJournalPageView from "./pages/RecurringJournalPageView";
import RecurringJournalDetailedPage from "./pages/RecurringJournalDetailedPage";
import EditRecurringJournalPage from "./pages/EditRecurringJournalPage";
import {
  ToastProvider,
  ToastContainer,
} from "./shared/presentation/components";
import {
  CustomerVendorUploadPage,
  AddUserPage,
  EditUserPage,
  AddRolePage,
  EditRolePage,
  CreateChecklistTemplatePage,
  MonthlyChecklistsPage,
  ChecklistDetailPage,
  ComplianceTypesPage,
  CompanyContextPage,
  ContextEnrichmentPage,
  GstinVerificationSettingsPage,
} from "./features/admin";
import { PermissionsProvider } from "./shared/presentation/contexts/PermissionsContext";
import "./App.css";
import RevenuePage from "./features/client_dashboard/presentation/pages/RevenuePage";
import ExpensePage from "./features/client_dashboard/presentation/pages/ExpensePage";
import CashAndBankPage from "./features/client_dashboard/presentation/pages/CashAndBankPage";
import BooksHealthPage from "./features/client_dashboard/presentation/pages/BooksHealthPage";
import TaskOverviewPage from "./pages/TaskOverviewPage";
import ClientOverviewPage from "./features/client_dashboard/presentation/pages/ClientOverviewPage";
import BooksDashboardPage from "./pages/BooksDashboardPage";
import { ReportsOverviewPage, ZohoReportPage, REPORTS_BASE_PATH } from "./features/reports";

const App = () => {
  return (
    <ToastProvider>
      <AuthProvider>
        <PermissionsProvider>
          <Router>
            <div className="App">
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<LoginPage />} />
                <Route path="/admin" element={<AdminLoginPage />} />
                <Route path="/unauthorized" element={<UnauthorizedPage />} />
                <Route
                  path="/client-only-restricted"
                  element={
                    <ProtectedRoute>
                      <ClientOnlyRestrictedPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/zoho-callback" element={<ZohoCallbackPage />} />
                <Route path="/from-master" element={<FromMasterPage />} />

                {/*
                  v1 app (issue #636).

                  Runs in PARALLEL with /dashboard/* — nothing below is changed
                  or replaced. v1 screens use their own sidebar shell, so they
                  are mounted here rather than nested inside DashboardPage,
                  which owns the horizontal tab chrome.
                */}
                <Route
                  path="/v1"
                  element={
                    <ProtectedRoute>
                      <AppShellV1>
                        <HomePage />
                      </AppShellV1>
                    </ProtectedRoute>
                  }
                />

                {/* Tenant settings area. `/settings` lands on the first panel.
                    Deliberately outside AppShellV1: settings is a full-screen
                    mode with its own rail, not a screen inside the workbench.

                    ADMIN ONLY, every panel. The write endpoints behind these
                    screens are already admin-gated server-side
                    (`BaseController::isAdminByRoleId`), so without this a
                    non-admin got editable forms, Add and Sync buttons, and a
                    403 on save. `requiredRole` uses the same rule the backend
                    does: role name equals "admin", CFO included in nobody. The
                    Settings entry in the sidebar is hidden to match, so this
                    catches typed URLs rather than being the only defence. */}
                <Route
                  path="/tally-connector"
                  element={
                    <ProtectedRoute>
                      <RequireAccountingBackend backend="tally">
                        <AppShellV1>
                          <TallyConnectorPage />
                        </AppShellV1>
                      </RequireAccountingBackend>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={<Navigate to={SETTINGS_DEFAULT_PATH} replace />}
                />
                <Route
                  path="/settings/company-details"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <CompanyDetailsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/chart-of-accounts"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <ChartOfAccountsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/gst"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <RequireAccountingBackend backend="zoho">
                        <GstPage />
                      </RequireAccountingBackend>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/locations"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <LocationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/lut"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <LutPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/tds-tcs"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <RequireAccountingBackend backend="zoho">
                        <TdsTcsPage />
                      </RequireAccountingBackend>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/banks"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <RequireAccountingBackend backend="zoho">
                        <BanksPage />
                      </RequireAccountingBackend>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/customers-vendors"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <CustomersVendorsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/currency"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <RequireAccountingBackend backend="zoho">
                        <CurrencyPage />
                      </RequireAccountingBackend>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/team-assignment"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <TeamAssignmentPage />
                    </ProtectedRoute>
                  }
                />
                {/* /settings/approval-rules retired 2026-08-21: the six-tab
                    team board is the only policy editor now (its save derives
                    review policies); the panel's full-replace save was the
                    clobber path. Components stay in the tree, unrouted. */}
                <Route
                  path="/settings/escalations-access"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <EscalationsAccessPage />
                    </ProtectedRoute>
                  }
                />

                {/* Protected Routes - Require Authentication */}
                <Route
                  path="/dashboard/*"
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cfo"
                  element={<Navigate to="/cfo/dashboard" replace />}
                />
                <Route
                  path="/cfo/dashboard/queries"
                  element={
                    <ProtectedRoute>
                      <QueriesSection />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cfo/dashboard/*"
                  element={
                    <ProtectedRoute>
                      <CFODashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cfo/tb/upload_mapping"
                  element={
                    <ProtectedRoute>
                      <TrialBalanceUploadPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cfo/tb/delete-gls"
                  element={
                    <ProtectedRoute>
                      <DeleteGlsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cfo/tb/reports"
                  element={
                    <ProtectedRoute>
                      <ReportsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cfo/tb/reports/is"
                  element={
                    <ProtectedRoute>
                      <IncomeStatementReportsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cfo/registers/upload"
                  element={
                    <ProtectedRoute>
                      <UploadRegistersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cfo/registers/uploadv1"
                  element={
                    <ProtectedRoute>
                      <PivotViewLibraryPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cfo/registers/uploadv1/create/:fileId?"
                  element={
                    <ProtectedRoute>
                      <CreatePivotBuilderPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cfo/registers/uploadv1/edit/:viewId"
                  element={
                    <ProtectedRoute>
                      <EditPivotViewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cfo/registers/uploadv1/apply/:viewId"
                  element={
                    <ProtectedRoute>
                      <ApplyPivotViewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cfo/register-view"
                  element={
                    <ProtectedRoute>
                      <RegisterViewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cfo/reporting"
                  element={
                    <ProtectedRoute>
                      <ReportingPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cfo/ocr-quality"
                  element={
                    <ProtectedRoute>
                      <OcrQualityDashboardPage />
                    </ProtectedRoute>
                  }
                />

                {/* Admin Only Routes - handled by AdminLoginPage at /admin */}
                <Route
                  path="/admin/dashboard"
                  element={<Navigate to="/admin" replace />}
                />

                {/* Invoices Feature Routes */}
                <Route
                  path="/invoices/all"
                  element={
                    <ProtectedRoute>
                      <AllInvoicesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/invoices/review"
                  element={
                    <ProtectedRoute>
                      <InvoiceReviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/invoices/manual/new"
                  element={
                    <ProtectedRoute>
                      <CreateManualInvoicePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/add-customer"
                  element={
                    <ProtectedRoute>
                      <AddCustomerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/edit-customer"
                  element={
                    <ProtectedRoute>
                      <EditCustomerPage />
                    </ProtectedRoute>
                  }
                />

                {/* Bills Routes */}
                <Route
                  path="/bills/all"
                  element={
                    <ProtectedRoute>
                      <AllBillsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/bills/review"
                  element={
                    <ProtectedRoute>
                      <BillsReviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/bills/manual/new"
                  element={
                    <ProtectedRoute>
                      <CreateManualBillPage />
                    </ProtectedRoute>
                  }
                />
                {/* ZOUR-67 — the background reviewer's queue. Sits beside the
                    review page rather than inside it: it is a list of bills to
                    triage, not a bill to edit. Zoho-only for now. */}
                <Route
                  path="/bills/agent-review"
                  element={
                    <ProtectedRoute>
                      <RequireAccountingBackend backend="zoho">
                        <AgentReviewPage />
                      </RequireAccountingBackend>
                    </ProtectedRoute>
                  }
                />

                {/* Admin User Management Routes */}
                <Route
                  path="/admin/users/add"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <AddUserPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users/edit/:userId"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <EditUserPage />
                    </ProtectedRoute>
                  }
                />

                {/* Admin Role Management Routes */}
                <Route
                  path="/admin/roles/add"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <AddRolePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/roles/edit/:roleId"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <EditRolePage />
                    </ProtectedRoute>
                  }
                />

                {/* Admin Company Context Route */}
                <Route
                  path="/admin/company-context"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <CompanyContextPage />
                    </ProtectedRoute>
                  }
                />

                {/* Admin Context Enrichment Route */}
                <Route
                  path="/admin/context-enrichment"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <ContextEnrichmentPage />
                    </ProtectedRoute>
                  }
                />

                {/* Admin GSTIN Verification Settings Route */}
                <Route
                  path="/admin/gstin-verification"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <GstinVerificationSettingsPage />
                    </ProtectedRoute>
                  }
                />

                {/* Admin Compliance Routes */}
                <Route
                  path="/admin/compliance/types"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <ComplianceTypesPage />
                    </ProtectedRoute>
                  }
                />

                {/* Client Queries Route */}
                <Route
                  path="/client/queries"
                  element={
                    <ProtectedRoute clientOnly={true}>
                      <ClientQueriesSection />
                    </ProtectedRoute>
                  }
                />

                {/* Revenue Route - Client accessible */}
                <Route
                  path="/revenue"
                  element={
                    <ProtectedRoute clientOnly={true}>
                      <RevenuePage />
                    </ProtectedRoute>
                  }
                />
                {/* Expense Route - Client accessible */}
                <Route
                  path="/expense"
                  element={
                    <ProtectedRoute clientOnly={true}>
                      <ExpensePage />
                    </ProtectedRoute>
                  }
                />
                {/* Cash & Bank Route - Client accessible */}
                <Route
                  path="/cash-and-bank"
                  element={
                    <ProtectedRoute clientOnly={true}>
                      <CashAndBankPage />
                    </ProtectedRoute>
                  }
                />

                {/* Client Overview Route */}
                <Route
                  path="/client-overview"
                  element={
                    <ProtectedRoute clientOnly={true}>
                      <ClientOverviewPage />
                    </ProtectedRoute>
                  }
                />

                {/* Client Compliance Route */}
                <Route
                  path="/client/compliance"
                  element={
                    <ProtectedRoute clientOnly={true}>
                      <ClientComplianceDashboard />
                    </ProtectedRoute>
                  }
                />

                {/* Simple reports area (ZOUR-22). Zoho's own statements, live.
                    Full-screen with its own rail, like /settings. Open to every
                    signed-in role on Zoho tenants; Tally tenants are redirected
                    away (nav item is also hidden). */}
                <Route
                  path={REPORTS_BASE_PATH}
                  element={
                    <ProtectedRoute>
                      <RequireAccountingBackend backend="zoho">
                        <ReportsOverviewPage />
                      </RequireAccountingBackend>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={`${REPORTS_BASE_PATH}/profit-and-loss`}
                  element={
                    <ProtectedRoute>
                      <RequireAccountingBackend backend="zoho">
                        <ZohoReportPage slug="profit-and-loss" />
                      </RequireAccountingBackend>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={`${REPORTS_BASE_PATH}/balance-sheet`}
                  element={
                    <ProtectedRoute>
                      <RequireAccountingBackend backend="zoho">
                        <ZohoReportPage slug="balance-sheet" />
                      </RequireAccountingBackend>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={`${REPORTS_BASE_PATH}/cash-flow`}
                  element={
                    <ProtectedRoute>
                      <RequireAccountingBackend backend="zoho">
                        <ZohoReportPage slug="cash-flow" />
                      </RequireAccountingBackend>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={`${REPORTS_BASE_PATH}/trial-balance`}
                  element={
                    <ProtectedRoute>
                      <RequireAccountingBackend backend="zoho">
                        <ZohoReportPage slug="trial-balance" />
                      </RequireAccountingBackend>
                    </ProtectedRoute>
                  }
                />

                {/* Books Health Route - must be before /books/* */}
                <Route
                  path="/books/health"
                  element={
                    <ProtectedRoute clientOnly={true}>
                      <BooksHealthPage />
                    </ProtectedRoute>
                  }
                />

                {/* Books Dashboard Route */}
                <Route
                  path="/books/*"
                  element={
                    <ProtectedRoute clientOnly={true}>
                      <BooksDashboardPage />
                    </ProtectedRoute>
                  }
                />

                {/* AR Aging Route - Keep for backward compatibility */}
                <Route
                  path="/ar-aging"
                  element={
                    <ProtectedRoute clientOnly={true}>
                      <Navigate to="/books/ar-aging" replace />
                    </ProtectedRoute>
                  }
                />

                {/* AP Aging Route - Keep for backward compatibility */}
                <Route
                  path="/ap-aging"
                  element={
                    <ProtectedRoute clientOnly={true}>
                      <Navigate to="/books/ap-aging" replace />
                    </ProtectedRoute>
                  }
                />

                {/* Task Overview Route */}
                <Route
                  path="/task-overview"
                  element={
                    <ProtectedRoute clientOnly={true}>
                      <TaskOverviewPage />
                    </ProtectedRoute>
                  }
                />

                {/* Client Queries Route */}
                <Route
                  path="/client/queries"
                  element={
                    <ProtectedRoute clientOnly={true}>
                      <ClientQueriesSection />
                    </ProtectedRoute>
                  }
                />

                {/* Invoices Feature Routes */}
                <Route
                  path="/invoices/all"
                  element={
                    <ProtectedRoute>
                      <AllInvoicesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/invoices/review"
                  element={
                    <ProtectedRoute>
                      <InvoiceReviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/invoices/manual/new"
                  element={
                    <ProtectedRoute>
                      <CreateManualInvoicePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/add-customer"
                  element={
                    <ProtectedRoute>
                      <AddCustomerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/edit-customer"
                  element={
                    <ProtectedRoute>
                      <EditCustomerPage />
                    </ProtectedRoute>
                  }
                />
                {/* Reco Routes */}
                <Route
                  path="/reco/customer-credits"
                  element={
                    <ProtectedRoute>
                      <CustomerCreditsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reco/vendor-credits"
                  element={
                    <ProtectedRoute>
                      <VendorCreditsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reco/customer-outstanding-invoices"
                  element={
                    <ProtectedRoute>
                      <CustomerOutstandingInvoicesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reco/vendor-outstanding-bills"
                  element={
                    <ProtectedRoute>
                      <VendorOutstandingBillsPage />
                    </ProtectedRoute>
                  }
                />

                {/* Credit Notes Routes */}
                <Route
                  path="/credit/review/:id"
                  element={
                    <ProtectedRoute>
                      <CreditNoteReviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/credit/review/new"
                  element={
                    <ProtectedRoute>
                      <CreditNoteReviewPage />
                    </ProtectedRoute>
                  }
                />

                {/* Debit Notes Routes */}
                <Route
                  path="/debit/review/:id"
                  element={
                    <ProtectedRoute>
                      <DebitNoteReviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/debit/review/new"
                  element={
                    <ProtectedRoute>
                      <DebitNoteReviewPage />
                    </ProtectedRoute>
                  }
                />

                {/* Queries Routes */}
                <Route
                  path="/queries"
                  element={
                    <ProtectedRoute>
                      <QueriesSection />
                    </ProtectedRoute>
                  }
                />

                {/* Add New Party Route */}
                <Route
                  path="/add-party"
                  element={
                    <ProtectedRoute>
                      <AddNewPartyPage />
                    </ProtectedRoute>
                  }
                />

                {/* Edit Party Route */}
                <Route
                  path="/edit-party"
                  element={
                    <ProtectedRoute>
                      <EditPartyPage />
                    </ProtectedRoute>
                  }
                />

                {/* New Journal Entry Route */}
                <Route
                  path="/journal/new"
                  element={
                    <ProtectedRoute>
                      <NewJournalEntry />
                    </ProtectedRoute>
                  }
                />

                {/* Journal Detail Route */}
                <Route
                  path="/journal/detail/:journalId"
                  element={
                    <ProtectedRoute>
                      <JournalDetailPage />
                    </ProtectedRoute>
                  }
                />

                {/* Edit Journal Entry Route */}
                <Route
                  path="/journal/edit/:journalId"
                  element={
                    <ProtectedRoute>
                      <EditJournalPage />
                    </ProtectedRoute>
                  }
                />

                {/* Recurring Journals Route */}
                <Route
                  path="/journal/recurring"
                  element={
                    <ProtectedRoute>
                      <RecurringJournalPageView />
                    </ProtectedRoute>
                  }
                />

                {/* Recurring Journal Detail Route */}
                <Route
                  path="/journal/recurring/:templateId"
                  element={
                    <ProtectedRoute>
                      <RecurringJournalDetailedPage />
                    </ProtectedRoute>
                  }
                />

                {/* Edit Recurring Journal Route */}
                <Route
                  path="/journal/recurring/:templateId/edit"
                  element={
                    <ProtectedRoute>
                      <EditRecurringJournalPage />
                    </ProtectedRoute>
                  }
                />

                {/* Customer & Vendor Upload Route */}
                <Route
                  path="/admin/customer-vendor-upload"
                  element={
                    <ProtectedRoute>
                      <CustomerVendorUploadPage />
                    </ProtectedRoute>
                  }
                />

                {/* Month End Checklist Template Creation Route */}
                <Route
                  path="/admin/month-end-checklist/create-template"
                  element={
                    <ProtectedRoute>
                      <CreateChecklistTemplatePage />
                    </ProtectedRoute>
                  }
                />

                {/* Month End Checklists Routes */}
                <Route
                  path="/admin/month-end-checklists"
                  element={
                    <ProtectedRoute>
                      <MonthlyChecklistsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/month-end-checklists/:checklistId"
                  element={
                    <ProtectedRoute>
                      <ChecklistDetailPage />
                    </ProtectedRoute>
                  }
                />

                {/* Catch all route */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <ToastContainer />
            </div>
          </Router>
        </PermissionsProvider>
      </AuthProvider>
    </ToastProvider>
  );
};

export default App;
